/* eslint-disable max-lines */
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { useConfirm } from '@utils/providers/confirm-context.tsx';
import PageHeader from '@components/layout/page-header';
import Button from '@components/atoms/ui-kit/button';
import Input from '@components/atoms/ui-kit/input';
import SelectInput from '@components/atoms/ui-kit/select-input';
import DateInput from '@components/atoms/ui-kit/date-input';
import Tabs, { Tab } from '@components/atoms/ui-kit/tabs';
import toast from 'react-hot-toast';
import {
  Search,
  Copy,
  Check,
  RefreshCw,
  AlertCircle,
  Database,
  FileText,
  List,
  Download,
  Subtitles,
  Terminal,
  RotateCcw,
  XCircle,
  Trash2,
  ExternalLink,
  ChevronRight,
  Snowflake
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PATHS } from '../../router/paths';
import { useSearchSessions } from '@utils/api/back-office/session-inspector/use-search-sessions';
import {
  useGetSessionDetail,
  useRehydrateSession
} from '@utils/api/back-office/session-inspector/use-get-session-detail';
import { useGetSessionLogs } from '@utils/api/back-office/session-inspector/use-get-session-logs';
import { useGetBlobDownloadUrl } from '@utils/api/back-office/session-inspector/use-get-blob-download-url';
import {
  useExportSessionDocument,
  type SessionExportFormat
} from '@utils/api/back-office/session-inspector/use-export-session-document';
import {
  useRequeueSession,
  useKillSession,
  useCleanupSession
} from '@utils/api/back-office/operations';
import type {
  SessionSearchResult,
  BlobInfo,
  SessionDocumentInfo,
  SessionLogSource
} from '@utils/types/back-office/session-inspector';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { type GridAction } from '@components/atoms/datatable/grid-action-controller';
import { StatusBadge } from '@components/atoms/ui-kit/badge';

const SEARCH_PAGE_SIZE = 25;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDateTime(value: string | null): string {
  if (!value) return '-';
  return new Date(value).toLocaleString();
}

function formatDuration(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return '-';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const parts: string[] = [];
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);
  if (s > 0 || parts.length === 0) parts.push(`${s}s`);
  return parts.join(' ');
}

function formatFileSize(bytes: number | null): string {
  if (bytes === null || bytes === undefined) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function isArchived(blob: BlobInfo): boolean {
  const tier = (blob.tier ?? '').toLowerCase();
  const archive = (blob.archive_status ?? '').toLowerCase();
  return (
    tier === 'archive' ||
    archive.startsWith('rehydrate-pending') ||
    archive === 'archived'
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function CopyButton({
  value,
  field,
  copiedField,
  onCopy
}: {
  value: string;
  field: string;
  copiedField: string | null;
  onCopy: (value: string, field: string) => void;
}) {
  const { getColour } = useThemeContext();
  return (
    <button
      onClick={() => onCopy(value, field)}
      className="p-1 rounded-2xl hover:opacity-70"
      style={{ color: getColour('inactiveText') }}
      title="Copy"
    >
      {copiedField === field ? (
        <Check className="h-3.5 w-3.5 text-green-500 dark:text-green-400" />
      ) : (
        <Copy className="h-3.5 w-3.5" />
      )}
    </button>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function SessionInspector() {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const { show: showConfirm } = useConfirm();
  const navigate = useNavigate();

  // Search form state
  const [queryInput, setQueryInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchSubmitted, setSearchSubmitted] = useState(false);
  const [submittedParams, setSubmittedParams] = useState<{
    q?: string;
    status?: string;
    date_from?: string;
    date_to?: string;
    limit?: number;
    offset?: number;
  }>({});

  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(
    null
  );
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // K8s log viewer state (lazy)
  const [k8sLogSource, setK8sLogSource] = useState<SessionLogSource>('all');
  const [k8sLogsRequested, setK8sLogsRequested] = useState(false);
  const [downloadingBlob, setDownloadingBlob] = useState<string | null>(null);

  const {
    data: k8sLogsData,
    isLoading: isK8sLogsLoading,
    error: k8sLogsError,
    refetch: refetchK8sLogs
  } = useGetSessionLogs(selectedSessionId, k8sLogSource, k8sLogsRequested);

  const { data: searchData, isLoading: isSearchLoading } = useSearchSessions(
    submittedParams,
    searchSubmitted
  );

  const {
    data: detailData,
    isLoading: isDetailLoading,
    refetch: refetchDetail
  } = useGetSessionDetail(selectedSessionId);

  const { mutate: requeueSession } = useRequeueSession();
  const { mutate: killSession } = useKillSession();
  const { mutate: cleanupSession } = useCleanupSession();
  const { mutate: rehydrateSession } = useRehydrateSession();
  const { mutateAsync: getBlobUrl } = useGetBlobDownloadUrl();
  const { mutateAsync: exportDocument, isLoading: isExporting } =
    useExportSessionDocument();
  // Track which document+format combo is downloading so only that button
  // shows the spinner — multiple buttons share the single mutation.
  const [exportingKey, setExportingKey] = useState<string | null>(null);

  const handleExportDocument = async (
    document: SessionDocumentInfo,
    format: SessionExportFormat
  ) => {
    if (!selectedSessionId) return;
    const key = `${document.id}:${format}`;
    setExportingKey(key);
    const toastId = toast.loading(
      t('exporting-document', { format: format.toUpperCase() }) ||
        `Exporting ${format.toUpperCase()}…`
    );
    try {
      await exportDocument({
        zoomId: selectedSessionId,
        documentId: document.id,
        format
      });
      toast.success(
        t('document-exported', { format: format.toUpperCase() }) ||
          `${format.toUpperCase()} downloaded`,
        { id: toastId }
      );
    } catch {
      toast.error(t('export-failed') || 'Export failed', { id: toastId });
    } finally {
      setExportingKey(null);
    }
  };

  const hasArchivedBlob = useMemo(
    () => !!detailData?.blobs?.some(isArchived),
    [detailData?.blobs]
  );

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const buildSearchParams = (): typeof submittedParams => {
    const params: typeof submittedParams = {
      limit: SEARCH_PAGE_SIZE,
      offset: 0
    };
    if (queryInput.trim()) params.q = queryInput.trim();
    if (statusFilter) params.status = statusFilter;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    return params;
  };

  const handleSearch = () => {
    setSubmittedParams(buildSearchParams());
    setSearchSubmitted(true);
    setSelectedSessionId(null);
  };

  const handleSelectRow = (session: SessionSearchResult) => {
    setSelectedSessionId(session.session_id);
    setK8sLogsRequested(false);
  };

  const handleDownloadLog = () => {
    if (!detailData?.session_log) return;
    const blob = new Blob([detailData.session_log], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${detailData.session.session_id}.log`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadBlob = useCallback(
    async (sessionId: string, name: string) => {
      setDownloadingBlob(name);
      try {
        const { url } = await getBlobUrl({ sessionId, name });
        window.open(url, '_blank', 'noopener,noreferrer');
      } catch {
        toast.error(t('failed-to-get-download-url'));
      } finally {
        setDownloadingBlob(null);
      }
    },
    [getBlobUrl, t]
  );

  const handleCopy = (value: string, field: string) => {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopiedField(field);
        toast.success(t('copied-to-clipboard'));
        setTimeout(() => setCopiedField(null), 2000);
      })
      .catch(() => {
        toast.error(t('copy-failed'));
      });
  };

  const runAction = (
    action: 'requeue' | 'kill' | 'cleanup' | 'rehydrate',
    sessionId: string
  ) => {
    setActionLoading(action);
    const onSettled = () => {
      setActionLoading(null);
      refetchDetail();
    };
    if (action === 'requeue') {
      requeueSession(sessionId, {
        onSettled,
        onSuccess: () => {
          toast.success(t('session-requeue-success'));
        }
      });
    } else if (action === 'kill') {
      killSession(sessionId, {
        onSettled,
        onSuccess: () => {
          toast.success(t('session-kill-success'));
        }
      });
    } else if (action === 'cleanup') {
      cleanupSession(sessionId, {
        onSettled,
        onSuccess: () => {
          toast.success(t('session-cleanup-success'));
        }
      });
    } else {
      rehydrateSession(sessionId, {
        onSettled,
        onSuccess: () => {
          toast.success(t('rehydration-started'));
        }
      });
    }
  };

  const handleAction = (
    action: 'requeue' | 'kill' | 'cleanup' | 'rehydrate',
    sessionId: string
  ) => {
    if (action === 'kill' || action === 'cleanup') {
      showConfirm({
        type: action === 'kill' ? 'error' : 'warning',
        title: action === 'kill' ? t('kill-session') : t('cleanup-session'),
        subtitle:
          action === 'kill'
            ? t('confirm-kill-session')
            : t('confirm-cleanup-session'),
        confirmText: t(action),
        onConfirm: () => runAction(action, sessionId)
      });
      return;
    }
    runAction(action, sessionId);
  };

  // ─── Shared styles ─────────────────────────────────────────────────────────

  const cardStyle = {
    backgroundColor: getColour('secondLayer'),
    borderWidth: 1,
    borderStyle: 'solid' as const,
    borderColor: getColour('border')
  };

  const statusOptions = [
    { label: t('queued'), value: 'queued' },
    { label: t('processing'), value: 'processing' },
    { label: t('streaming'), value: 'streaming' },
    { label: t('finished'), value: 'finished' },
    { label: t('error'), value: 'error' },
    { label: t('stranded'), value: 'stranded' }
  ];

  // ─── Blob storage columns ──────────────────────────────────────────────────

  const blobColumns = useMemo<DataGridColumn<BlobInfo>[]>(
    () => [
      {
        id: 'name',
        header: t('name'),
        cell: ({ row }) => (
          <span
            className="font-mono truncate block max-w-[260px]"
            title={row.original.name}
          >
            {row.original.name}
          </span>
        )
      },
      {
        id: 'size',
        header: t('size'),
        cell: ({ row }) => formatFileSize(row.original.size)
      },
      {
        id: 'tier',
        header: t('tier'),
        cell: ({ row }) => {
          const archived = isArchived(row.original);
          return row.original.tier ? (
            <span
              className={
                archived
                  ? 'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-2xl text-xs bg-blue-500/15 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400'
                  : 'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-2xl text-xs border border-hairline'
              }
              style={
                archived
                  ? undefined
                  : {
                      color: getColour('normalText'),
                      backgroundColor: getColour('thirdLayer')
                    }
              }
            >
              {archived && <Snowflake className="h-3 w-3" />}
              {row.original.tier}
            </span>
          ) : (
            '-'
          );
        }
      },
      {
        id: 'last_modified',
        header: t('last-modified'),
        cell: ({ row }) => formatDateTime(row.original.last_modified)
      }
    ],
    [t, getColour]
  );

  const blobActions = useMemo<GridAction<BlobInfo>[]>(
    () => [
      {
        id: 'download',
        label: t('download'),
        icon: <Download className="h-3 w-3" />,
        isDisabled: blob => {
          if (!blob) return false;
          if (isArchived(blob))
            return t('blob-archived-rehydrate-first') as string;
          if (downloadingBlob !== null) return true;
          return false;
        },
        run: blob => {
          const sessionId = detailData?.session?.session_id;
          if (sessionId) handleDownloadBlob(sessionId, blob.name);
        }
      }
    ],
    [t, downloadingBlob, detailData?.session?.session_id, handleDownloadBlob]
  );

  // ─── Tab content ───────────────────────────────────────────────────────────

  const detailTabs: Tab[] = detailData
    ? [
        {
          id: 0,
          name: (
            <span className="flex items-center gap-2">
              <List className="h-4 w-4" />
              {t('overview')}
            </span>
          ),
          component: (
            <div className="py-3 space-y-4">
              {detailData.session.error_message && (
                <div className="rounded-2xl p-4 border bg-red-500/10 border-red-500/25 dark:bg-red-500/15 dark:border-red-500/35">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                    <span className="text-sm font-medium text-red-700 dark:text-red-400">
                      {t('error-message')}
                    </span>
                    {detailData.session.error_category && (
                      <span className="text-xs px-1.5 py-0.5 rounded-2xl uppercase font-semibold bg-red-500/15 text-red-700 dark:bg-red-500/10 dark:text-red-400">
                        {detailData.session.error_category}
                      </span>
                    )}
                  </div>
                  <pre
                    className="text-xs font-mono whitespace-pre-wrap break-all p-2 rounded-2xl"
                    style={{
                      backgroundColor: getColour('baseLayer'),
                      color: getColour('normalText')
                    }}
                  >
                    {detailData.session.error_message}
                  </pre>
                </div>
              )}

              <div
                className="rounded-2xl p-3 border border-hairline"
                style={{ backgroundColor: getColour('thirdLayer') }}
              >
                <div className="flex items-center justify-between mb-1">
                  <p
                    className="text-xs"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('organization')}
                  </p>
                  <button
                    onClick={() =>
                      navigate(
                        PATHS(String(detailData.session.organization_id))
                          .CUSTOMER_CONTENT
                      )
                    }
                    className="flex items-center gap-1 text-xs hover:underline"
                    style={{ color: getColour('activeText') }}
                  >
                    {t('view')}
                    <ExternalLink className="h-3 w-3" />
                  </button>
                </div>
                <p
                  className="font-medium text-sm"
                  style={{ color: getColour('normalText') }}
                >
                  {detailData.session.organization_name}
                </p>
                <p
                  className="text-xs"
                  style={{ color: getColour('inactiveText') }}
                >
                  ID: {detailData.session.organization_id}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {(
                  [
                    [t('mode'), detailData.session.session_mode],
                    [t('language'), detailData.session.language],
                    [
                      t('duration'),
                      formatDuration(detailData.session.duration)
                    ],
                    [
                      t('filesize'),
                      formatFileSize(detailData.session.media_filesize)
                    ],
                    [t('mimetype'), detailData.session.mimetype ?? '-'],
                    [t('filename'), detailData.session.filename ?? '-']
                  ] as [string, string][]
                ).map(([label, value]) => (
                  <div
                    key={label}
                    className="rounded-2xl p-3 border border-hairline"
                    style={{ backgroundColor: getColour('thirdLayer') }}
                  >
                    <p
                      className="text-xs mb-1"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {label}
                    </p>
                    <p
                      className="text-sm font-medium truncate"
                      style={{ color: getColour('normalText') }}
                    >
                      {value}
                    </p>
                  </div>
                ))}
              </div>

              <div
                className="rounded-2xl p-3 border border-hairline space-y-2"
                style={{ backgroundColor: getColour('thirdLayer') }}
              >
                <p
                  className="text-xs font-medium"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('timestamps')}
                </p>
                {(
                  [
                    ['started', detailData.session.date_session_start],
                    ['uploaded', detailData.session.date_upload],
                    ['transcoded', detailData.session.date_transcoded],
                    ['transcribed', detailData.session.date_transcribed],
                    ['finished', detailData.session.date_finished],
                    ['retried', detailData.session.date_retried],
                    ['last-updated', detailData.session.last_update]
                  ] as [string, string | null][]
                ).map(([label, value]) => (
                  <div key={label} className="flex justify-between">
                    <span
                      className="text-xs"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {t(label)}
                    </span>
                    <span
                      className="text-xs"
                      style={{ color: getColour('normalText') }}
                    >
                      {formatDateTime(value)}
                    </span>
                  </div>
                ))}
              </div>

              {detailData.queue && (
                <div
                  className="rounded-2xl p-3 border border-hairline space-y-2"
                  style={{ backgroundColor: getColour('thirdLayer') }}
                >
                  <div className="flex items-center justify-between">
                    <p
                      className="text-xs font-medium"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {t('queue')}
                    </p>
                    <StatusBadge status={detailData.queue.status} />
                  </div>
                  {(
                    [
                      [t('retry-count'), String(detailData.queue.retry_count)],
                      [
                        t('processing-started'),
                        formatDateTime(detailData.queue.processing_started_at)
                      ],
                      [
                        t('last-error'),
                        formatDateTime(detailData.queue.last_error_at)
                      ]
                    ] as [string, string][]
                  ).map(([label, value]) => (
                    <div key={label} className="flex justify-between">
                      <span
                        className="text-xs"
                        style={{ color: getColour('inactiveText') }}
                      >
                        {label}
                      </span>
                      <span
                        className="text-xs"
                        style={{ color: getColour('normalText') }}
                      >
                        {value}
                      </span>
                    </div>
                  ))}
                  {detailData.queue.error_message && (
                    <pre
                      className="text-xs font-mono whitespace-pre-wrap p-2 rounded-2xl"
                      style={{
                        backgroundColor: getColour('baseLayer'),
                        color: getColour('normalText')
                      }}
                    >
                      {detailData.queue.error_message}
                    </pre>
                  )}
                </div>
              )}

              {/* Documents + on-the-fly export (#2020). Renders one card per
                  document attached to this session. Format buttons are
                  filtered server- and client-side by document_type:
                  "2" = caption → SRT/VTT, "1" = transcript → DOCX/TXT. */}
              {detailData.documents && detailData.documents.length > 0 && (
                <div
                  className="rounded-2xl border border-hairline overflow-hidden"
                  style={{ backgroundColor: getColour('thirdLayer') }}
                >
                  <div
                    className="px-3 py-2 flex items-center justify-between"
                    style={{
                      backgroundColor: getColour('secondLayer'),
                      borderBottom: `1px solid ${getColour('border')}`
                    }}
                  >
                    <span
                      className="text-xs font-semibold uppercase tracking-wide"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {t('documents')} ({detailData.documents.length})
                    </span>
                  </div>
                  <ul
                    className="divide-y"
                    style={{ borderColor: getColour('border') }}
                  >
                    {detailData.documents.map(doc => {
                      const isCaption = doc.document_type === '2';
                      const formats: SessionExportFormat[] = isCaption
                        ? ['srt', 'vtt']
                        : ['docx', 'txt'];
                      const TypeIcon = isCaption ? Subtitles : FileText;
                      return (
                        <li
                          key={doc.id}
                          className="flex items-center justify-between gap-3 px-3 py-2.5"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <TypeIcon
                              className="h-4 w-4 shrink-0"
                              style={{ color: getColour('inactiveText') }}
                              aria-hidden="true"
                            />
                            <div className="min-w-0">
                              <p
                                className="text-xs font-medium truncate"
                                style={{ color: getColour('normalText') }}
                                title={doc.storage_filename}
                              >
                                {isCaption ? t('caption') : t('transcript')}{' '}
                                <span
                                  className="font-mono"
                                  style={{ color: getColour('inactiveText') }}
                                >
                                  #{doc.id}
                                </span>
                              </p>
                              <p
                                className="text-xs truncate"
                                style={{ color: getColour('inactiveText') }}
                              >
                                {doc.created
                                  ? formatDateTime(doc.created)
                                  : '—'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            {formats.map(fmt => {
                              const key = `${doc.id}:${fmt}`;
                              const loading = exportingKey === key;
                              return (
                                <Button
                                  key={fmt}
                                  label={fmt.toUpperCase()}
                                  icon={<Download className="h-3.5 w-3.5" />}
                                  variant="secondary"
                                  size="small"
                                  loading={loading}
                                  disabled={isExporting && !loading}
                                  onClick={() => handleExportDocument(doc, fmt)}
                                />
                              );
                            })}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          )
        },
        {
          id: 1,
          name: (
            <span className="flex items-center gap-2">
              <Database className="h-4 w-4" />
              {t('storage')}
            </span>
          ),
          component: (
            <div className="py-3 space-y-4">
              {detailData.blobs_error && (
                <div className="rounded-2xl px-3 py-2 text-xs bg-red-500/10 border border-red-500/25 text-red-700 dark:bg-red-500/15 dark:border-red-500/35 dark:text-red-400">
                  {detailData.blobs_error}
                </div>
              )}
              {!detailData.blobs || detailData.blobs.length === 0 ? (
                <div
                  className="flex items-center justify-center h-24 text-sm rounded-2xl"
                  style={{
                    color: getColour('inactiveText'),
                    backgroundColor: getColour('thirdLayer')
                  }}
                >
                  {t('no-storage-data')}
                </div>
              ) : (
                <DataGridV2<BlobInfo>
                  data={detailData.blobs}
                  columns={blobColumns}
                  enablePagination={false}
                  enableSorting={false}
                  enableFiltering={false}
                  dense
                  getRowId={row => row.name}
                  actions={blobActions}
                />
              )}
            </div>
          )
        },
        {
          id: 2,
          name: (
            <span className="flex items-center gap-2">
              <Terminal className="h-4 w-4" />
              {t('logs')}
            </span>
          ),
          component: (
            <div className="py-3 space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <span
                    className="text-xs font-medium"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('session-log-from-azure-storage')}
                  </span>
                  {detailData.session_log && (
                    <Button
                      label={t('download')}
                      icon={<Download className="h-4 w-4" />}
                      variant="secondary"
                      size="small"
                      onClick={handleDownloadLog}
                    />
                  )}
                </div>
                {detailData.session_log ? (
                  <pre
                    className="text-xs font-mono whitespace-pre-wrap p-3 rounded-2xl overflow-auto max-h-[40vh]"
                    style={{ backgroundColor: '#0d1117', color: '#c9d1d9' }} // eslint-disable-line no-restricted-syntax -- intentional terminal dark-theme colors
                  >
                    {detailData.session_log}
                  </pre>
                ) : (
                  <div
                    className="flex items-center justify-center h-20 text-sm rounded-2xl"
                    style={{
                      color: getColour('inactiveText'),
                      backgroundColor: getColour('thirdLayer')
                    }}
                  >
                    {t('no-log-blob-for-session')}
                  </div>
                )}
              </div>

              {detailData.has_k8s_logs && (
                <div
                  className="pt-4"
                  style={{ borderTop: `1px solid ${getColour('border')}` }}
                >
                  <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                    <span
                      className="text-xs font-medium"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {t('kubernetes-pod-logs')}
                    </span>
                    <div className="flex items-center gap-2">
                      <div className="w-48">
                        <SelectInput
                          options={[
                            { label: t('all'), value: 'all' },
                            { label: 'api', value: 'api' },
                            { label: 'batch', value: 'batch' },
                            { label: 'post_upload', value: 'post_upload' },
                            { label: 'status_check', value: 'status_check' }
                          ]}
                          value={k8sLogSource}
                          onChange={opt => {
                            const next =
                              (opt as { value: SessionLogSource } | null)
                                ?.value ?? 'all';
                            setK8sLogSource(next);
                          }}
                          size="small"
                        />
                      </div>
                      <Button
                        label={k8sLogsRequested ? t('refresh') : t('load-logs')}
                        icon={<RefreshCw className="h-3.5 w-3.5" />}
                        variant="secondary"
                        className="w-48"
                        size="small"
                        loading={isK8sLogsLoading}
                        onClick={() => {
                          if (k8sLogsRequested) {
                            refetchK8sLogs();
                          } else {
                            setK8sLogsRequested(true);
                          }
                        }}
                      />
                    </div>
                  </div>

                  {!k8sLogsRequested && !isK8sLogsLoading && (
                    <div
                      className="flex items-center justify-center h-20 text-sm rounded-2xl"
                      style={{
                        color: getColour('inactiveText'),
                        backgroundColor: getColour('thirdLayer')
                      }}
                    >
                      {t('press-load-logs-to-fetch-k8s-logs')}
                    </div>
                  )}

                  {k8sLogsRequested && Boolean(k8sLogsError) && (
                    <div className="rounded-2xl p-3 text-xs bg-red-500/10 border border-red-500/25 text-red-700 dark:bg-red-500/15 dark:border-red-500/35 dark:text-red-400">
                      {t('failed-to-load-k8s-logs')}
                    </div>
                  )}

                  {k8sLogsRequested &&
                    !isK8sLogsLoading &&
                    !k8sLogsError &&
                    k8sLogsData && (
                      <>
                        {k8sLogsData.truncated && (
                          <div className="rounded-2xl px-3 py-2 text-xs mb-2 bg-amber-400/10 border border-amber-400/30 text-amber-700 dark:bg-amber-400/15 dark:border-amber-400/40 dark:text-amber-400">
                            {t('output-truncated')} · {k8sLogsData.total_lines}{' '}
                            {t('lines')}
                          </div>
                        )}
                        {k8sLogsData.lines.length === 0 ? (
                          <div
                            className="flex items-center justify-center h-20 text-sm rounded-2xl"
                            style={{
                              color: getColour('inactiveText'),
                              backgroundColor: getColour('thirdLayer')
                            }}
                          >
                            {t('no-k8s-logs-for-session')}
                          </div>
                        ) : (
                          <pre
                            className="text-xs font-mono whitespace-pre-wrap p-3 rounded-2xl overflow-auto max-h-[50vh]"
                            style={{
                              // eslint-disable-next-line no-restricted-syntax -- intentional terminal dark-theme colors
                              backgroundColor: '#0d1117',
                              // eslint-disable-next-line no-restricted-syntax -- intentional terminal dark-theme colors
                              color: '#c9d1d9'
                            }}
                          >
                            {k8sLogsData.lines.join('\n')}
                          </pre>
                        )}
                      </>
                    )}
                </div>
              )}
            </div>
          )
        }
      ]
    : [];

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="bg-app flex-1 space-y-4">
      <PageHeader
        title={t('session-inspector')}
        caption={t('search-and-inspect-any-session-by-id-filename-or-org')}
      />

      {/* Search card */}
      <div className="rounded-2xl p-4" style={cardStyle}>
        <form
          onSubmit={e => {
            e.preventDefault();
            handleSearch();
          }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Input
              label={t('session-id-filename-or-org')}
              value={queryInput}
              onChange={e => setQueryInput(e.target.value)}
              placeholder={t('session-id-filename-or-org')}
              size="small"
              required
            />
            <SelectInput
              label={t('status')}
              options={statusOptions}
              value={statusFilter}
              onChange={opt =>
                setStatusFilter((opt as { value: string } | null)?.value ?? '')
              }
              placeholder={t('all-statuses')}
              size="small"
            />
            <DateInput
              label={t('from')}
              value={dateFrom}
              onChange={setDateFrom}
              withShortcuts={false}
              size="small"
            />
            <DateInput
              label={t('until')}
              value={dateTo}
              onChange={setDateTo}
              withShortcuts={false}
              size="small"
            />
          </div>
          <div className="flex justify-end mt-3">
            <Button
              label={t('search')}
              icon={<Search className="h-4 w-4" />}
              variant="primary"
              size="small"
              loading={isSearchLoading}
              type="submit"
            />
          </div>
        </form>
      </div>

      {/* Results card */}
      {searchSubmitted && (
        <div className="rounded-2xl p-2" style={cardStyle}>
          {isSearchLoading && (
            <div
              className="flex items-center justify-center h-16 text-sm"
              style={{ color: getColour('inactiveText') }}
            >
              {t('loading')}...
            </div>
          )}

          {!isSearchLoading && !searchData?.results?.length && (
            <div
              className="flex flex-col items-center justify-center h-16 gap-2 text-sm"
              style={{ color: getColour('inactiveText') }}
            >
              <Search className="h-5 w-5 opacity-40" />
              {t('no-results-found')}
            </div>
          )}

          {!isSearchLoading && !!searchData?.results?.length && (
            <>
              <div className="space-y-1 max-h-72 overflow-y-auto">
                {searchData.results.map((session: SessionSearchResult) => (
                  <button
                    key={session.session_id}
                    onClick={() => handleSelectRow(session)}
                    className="w-full text-left px-1.5 py-1.5 rounded-2xl flex items-start gap-2 transition-colors hover:opacity-80"
                    style={{
                      backgroundColor:
                        selectedSessionId === session.session_id
                          ? getColour('thirdLayer')
                          : 'transparent',
                      border: `1px solid ${
                        selectedSessionId === session.session_id
                          ? getColour('border')
                          : 'transparent'
                      }`
                    }}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span
                          className="font-mono text-xs truncate"
                          style={{ color: getColour('normalText') }}
                        >
                          {session.session_id.slice(0, 22)}...
                        </span>
                        <StatusBadge status={session.status} />
                      </div>
                      <p
                        className="text-xs truncate"
                        style={{ color: getColour('inactiveText') }}
                      >
                        {session.organization_name}
                        {session.filename ? ` · ${session.filename}` : ''}
                      </p>
                      <p
                        className="text-xs mt-0.5"
                        style={{ color: getColour('inactiveText') }}
                      >
                        {formatDateTime(session.date_session_start)}
                      </p>
                    </div>
                    <ChevronRight
                      className="h-4 w-4 flex-shrink-0 mt-0.5"
                      style={{ color: getColour('inactiveText') }}
                    />
                  </button>
                ))}
              </div>
              <p
                className="text-xs mt-3 ml-1"
                style={{ color: getColour('inactiveText') }}
              >
                {searchData.total > SEARCH_PAGE_SIZE
                  ? `${t('showing-first')} ${SEARCH_PAGE_SIZE} ${t('of')} ${searchData.total} — ${t('narrow-search-to-see-more')}`
                  : `${searchData.total} ${t('results')}`}
              </p>
            </>
          )}
        </div>
      )}

      {/* Detail loading */}
      {selectedSessionId && isDetailLoading && (
        <div className="rounded-2xl p-6" style={cardStyle}>
          <div
            className="flex items-center justify-center text-sm"
            style={{ color: getColour('inactiveText') }}
          >
            {t('loading')}...
          </div>
        </div>
      )}

      {/* Detail card */}
      {selectedSessionId && !isDetailLoading && detailData && (
        <div className="rounded-2xl p-4" style={cardStyle}>
          {/* Header */}
          <div
            className="flex flex-wrap items-center gap-2 mb-4 pb-2"
            style={{ borderBottom: `1px solid ${getColour('border')}` }}
          >
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span
                className="font-mono text-sm truncate"
                style={{ color: getColour('normalText') }}
              >
                {detailData.session.session_id}
              </span>
              <CopyButton
                value={detailData.session.session_id}
                field="detail_session_id"
                copiedField={copiedField}
                onCopy={handleCopy}
              />
              <StatusBadge status={detailData.session.status_label} />
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <Button
                label={t('requeue')}
                icon={<RotateCcw className="h-3.5 w-3.5" />}
                variant="secondary"
                size="xs"
                loading={actionLoading === 'requeue'}
                disabled={actionLoading !== null}
                onClick={() =>
                  handleAction('requeue', detailData.session.session_id)
                }
                title={t('requeue-tooltip')}
              />
              {hasArchivedBlob && (
                <Button
                  label={t('rehydrate')}
                  icon={<Snowflake className="h-3.5 w-3.5" />}
                  variant="secondary"
                  size="xs"
                  loading={actionLoading === 'rehydrate'}
                  disabled={actionLoading !== null}
                  onClick={() =>
                    handleAction('rehydrate', detailData.session.session_id)
                  }
                  title={t('rehydrate-tooltip')}
                />
              )}
              <Button
                label={t('kill')}
                icon={<XCircle className="h-3.5 w-3.5" />}
                variant="danger"
                size="xs"
                loading={actionLoading === 'kill'}
                disabled={actionLoading !== null}
                onClick={() =>
                  handleAction('kill', detailData.session.session_id)
                }
                title={t('kill-tooltip')}
              />
              <Button
                label={t('cleanup')}
                icon={<Trash2 className="h-3.5 w-3.5" />}
                variant="danger"
                size="xs"
                loading={actionLoading === 'cleanup'}
                disabled={actionLoading !== null}
                onClick={() =>
                  handleAction('cleanup', detailData.session.session_id)
                }
                title={t('cleanup-tooltip')}
              />
            </div>
          </div>

          <Tabs
            key={selectedSessionId}
            tabs={detailTabs}
            minHeight="50vh"
            maxHeight="70vh"
          />
        </div>
      )}
    </div>
  );
}
